import type { NormalizedCacheObject } from "@apollo/client/core";
import { ApolloClient, ApolloLink, InMemoryCache, defaultDataIdFromObject } from "@apollo/client/core";
import { RetryLink } from "@apollo/client/link/retry";
import { getDataFromTree } from "@apollo/client/react/ssr";
import { injectAdditionalHeaders, setAuthorization } from "components/auth/Auth";
import createFundamenteiHttpLink from "lib/apollo/createFundamenteiHttpLink";
import createGitDetailsLink from "lib/apollo/createGitDetailsLink";
import createSsrFailureLink from "lib/apollo/createSsrFailureLink";
import { typePolicies } from "lib/apollo/typePolicies";
import isBrowser from "lib/utils/isBrowser";
import withApollo from "next-with-apollo";
import PossibleTypes from "types/possibleTypes";

const RETRYABLE_OPERATION_NAMES = ["RunScreener"];

export default withApollo(
  ({ ctx, initialState }) => {
    return fromInitialState(initialState || {}, [
      // Sits above the retry link so it only sees errors that survived every attempt
      createSsrFailureLink(ctx),
      new RetryLink({
        delay: {
          initial: 0,
          max: 2000,
          jitter: true,
        },
        attempts: {
          max: 3,
          retryIf: (error, operation) => {
            // Disable retries if SSRing
            if (!isBrowser()) {
              return false;
            }

            const isMutation = operation.query.definitions.some((candidate) => {
              return candidate.kind === "OperationDefinition" && candidate.operation === "mutation";
            });

            // Disable retries for mutations
            if (isMutation && !RETRYABLE_OPERATION_NAMES.includes(operation.operationName)) {
              return false;
            }

            // Disable retries for "Bad Request" responses
            if (
              error.name === "ServerError" &&
              typeof error.statusCode === "number" &&
              [400, 500].includes(error.statusCode)
            ) {
              return false;
            }

            return !!error;
          },
        },
      }),
      injectAdditionalHeaders(ctx),
      setAuthorization(ctx),
      createGitDetailsLink(),
    ]);
  },
  {
    getDataFromTree,
  },
);

// eslint-disable-next-line default-param-last
export function fromInitialState(initialState = {}, links: ApolloLink[]): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    connectToDevTools: isBrowser(),
    ssrMode: true,
    link: ApolloLink.from([...links, createFundamenteiHttpLink()]),
    cache: new InMemoryCache({
      addTypename: true,
      typePolicies,
      possibleTypes: PossibleTypes.possibleTypes,
      dataIdFromObject: (responseObject) => {
        if (responseObject.__typename === "IssuerLogo" && responseObject.url) {
          return `${responseObject.__typename}:${responseObject.url}`;
        }
        // Skip caching via `objectId`
        if (responseObject.__typename === "FinancialsComparisonSheetAdversaryColumnIndicatorSeries") {
          return defaultDataIdFromObject(responseObject);
        }
        if (
          responseObject.__typename === "UserSettingsV2" &&
          responseObject.ownerId &&
          typeof responseObject.ownerId === "string"
        ) {
          return `${responseObject.__typename}:${responseObject.ownerId}`;
        }
        // TODO: Add better handling for new objects using typed identifiers
        if (
          (responseObject as Record<string, any>).objectId &&
          typeof (responseObject as Record<string, any>).objectId === "string"
        ) {
          return `${responseObject.__typename}:${responseObject.objectId}`;
        }
        return defaultDataIdFromObject(responseObject);
      },
    }).restore(initialState),
  });
}
